import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { ArrowLeft, ClipboardList, TrendingUp, Award, Loader2, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../utils/api';

export default function DiagnosticPage() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      const data = await api.diagnosticListSessions();
      setSessions(data.sessions || []);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Diagnostic tests are available to tutors and subscribers only');
      } else {
        console.error('Failed to load sessions:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateNew = async () => {
    setGenerating(true);
    try {
      const data = await api.diagnosticGenerate();
      toast.success('Diagnostic test generated!');
      navigate(`/diagnostic/take/${data.session_id}`);
    } catch (error) {
      console.error('Generate error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate diagnostic');
      setGenerating(false);
    }
  };

  const getScoreColor = (percentage) => {
    if (percentage >= 80) return 'text-green-600';
    if (percentage >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 p-6">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/dashboard')}
            className="rounded-full"
            data-testid="diagnostic-back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Dashboard
          </Button>
        </div>

        {/* Hero Section */}
        <Card className="border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white" data-testid="diagnostic-hero-card">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div className="space-y-2">
                <CardTitle className="text-3xl text-emerald-900" data-testid="diagnostic-title">
                  Primary 6 English Diagnostic Package
                </CardTitle>
                <CardDescription className="text-base text-emerald-700">
                  Comprehensive PSLE assessment across all 7 question types • 35 questions • Instant results
                </CardDescription>
              </div>
              <ClipboardList className="w-12 h-12 text-emerald-600" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4 mb-6">
              <div className="flex items-start gap-3 bg-white p-4 rounded-lg border border-emerald-100">
                <Award className="w-5 h-5 text-emerald-600 mt-0.5" />
                <div>
                  <div className="font-semibold text-sm text-emerald-900">All 7 PSLE Types</div>
                  <div className="text-xs text-gray-600">Complete coverage of MOE syllabus</div>
                </div>
              </div>
              <div className="flex items-start gap-3 bg-white p-4 rounded-lg border border-emerald-100">
                <TrendingUp className="w-5 h-5 text-emerald-600 mt-0.5" />
                <div>
                  <div className="font-semibold text-sm text-emerald-900">Instant Analysis</div>
                  <div className="text-xs text-gray-600">Identify weak areas immediately</div>
                </div>
              </div>
              <div className="flex items-start gap-3 bg-white p-4 rounded-lg border border-emerald-100">
                <ClipboardList className="w-5 h-5 text-emerald-600 mt-0.5" />
                <div>
                  <div className="font-semibold text-sm text-emerald-900">Unlimited Tests</div>
                  <div className="text-xs text-gray-600">Regenerate anytime for free</div>
                </div>
              </div>
            </div>

            <Button
              onClick={handleGenerateNew}
              disabled={generating}
              size="lg"
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-full"
              data-testid="diagnostic-generate-button"
            >
              {generating ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Generating Questions...
                </>
              ) : (
                <>
                  <ClipboardList className="w-5 h-5 mr-2" />
                  Start New Diagnostic Test
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Past Sessions */}
        {loading ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-emerald-600" />
              <p className="text-sm text-gray-500 mt-4">Loading your diagnostic history...</p>
            </CardContent>
          </Card>
        ) : sessions.length > 0 ? (
          <Card>
            <CardHeader>
              <CardTitle data-testid="diagnostic-history-title">Your Diagnostic History</CardTitle>
              <CardDescription>Review past attempts and track progress</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {sessions.map((session) => {
                  const isCompleted = !!session.submitted_at;
                  const overallPct = session.results?.overall_percentage || 0;
                  
                  return (
                    <div
                      key={session.id}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => navigate(isCompleted ? `/diagnostic/results/${session.id}` : `/diagnostic/take/${session.id}`)}
                      data-testid={`diagnostic-session-${session.id}`}
                    >
                      <div className="flex-1">
                        <div className="font-semibold text-sm text-gray-900">
                          {isCompleted ? 'Completed' : 'In Progress'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {new Date(session.created_at).toLocaleDateString('en-SG', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                      </div>
                      
                      {isCompleted && (
                        <div className={`text-2xl font-bold mr-4 ${getScoreColor(overallPct)}`}>
                          {overallPct}%
                        </div>
                      )}
                      
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        ) : null}
      </div>
    </div>
  );
}
